import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { ENIGMAS, STORAGE_KEY } from '../data/enigmas';

const EMPTY = { solved: [], startedAt: null };

function readProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw);
    return {
      solved: Array.isArray(parsed.solved) ? parsed.solved : [],
      startedAt: parsed.startedAt ?? null,
    };
  } catch {
    return EMPTY;
  }
}

function writeProgress(progress) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  window.dispatchEvent(new Event('festa-progress'));
}

function subscribe(onStoreChange) {
  window.addEventListener('storage', onStoreChange);
  window.addEventListener('festa-progress', onStoreChange);
  return () => {
    window.removeEventListener('storage', onStoreChange);
    window.removeEventListener('festa-progress', onStoreChange);
  };
}

function getSnapshot() {
  return localStorage.getItem(STORAGE_KEY) ?? '';
}

export function useProgress() {
  const raw = useSyncExternalStore(subscribe, getSnapshot, () => '');
  const [progress, setProgress] = useState(EMPTY);

  useEffect(() => {
    setProgress(readProgress());
  }, [raw]);

  const ensureStarted = useCallback(() => {
    const current = readProgress();
    if (!current.startedAt) {
      writeProgress({ ...current, startedAt: new Date().toISOString() });
    }
  }, []);

  const markSolved = useCallback((id) => {
    const current = readProgress();
    if (current.solved.includes(id)) return;
    writeProgress({
      solved: [...current.solved, id],
      startedAt: current.startedAt ?? new Date().toISOString(),
    });
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event('festa-progress'));
  }, []);

  const isSolved = useCallback(
    (id) => progress.solved.includes(id),
    [progress.solved],
  );

  const remaining = ENIGMAS.filter((e) => !progress.solved.includes(e.id));
  const allDone = remaining.length === 0 && progress.solved.length > 0;

  return {
    progress,
    remaining,
    solvedCount: progress.solved.length,
    total: ENIGMAS.length,
    allDone,
    isSolved,
    ensureStarted,
    markSolved,
    reset,
  };
}
